import { useMemo } from 'react';
import { create } from 'zustand';
import { combine, subscribeWithSelector } from 'zustand/middleware';
import { eventBus } from '../event-manager/event-bus';
import {
  differenceBy,
  EVENT_LISTENER_METHODS,
  isArrayOfObjEqual,
  LAYOUT,
  MEDIA_TYPE,
} from '../utils';
import { type HumanParticipant } from '../utils/schema/human-participant';
import {
  sendParticipantEvent,
  sendParticipantListChangedEvent,
} from '../utils/send-participant-event';
import { setLayout, useBaseStore } from './base';
import { useConfigStore } from './config';
import { useTracksStore } from './tracks';

export interface VirtualParticipant
  extends Omit<HumanParticipant, 'role' | 'type'> {
  type: 'screen-share';
}

export type Participant = HumanParticipant | VirtualParticipant;

interface ParticipantsState {
  localParticipant: HumanParticipant;
  participants: HumanParticipant[];
  virtualParticipants: VirtualParticipant[];
  dominantSpeakers: string[];
  pinnedParticipant?: Participant;
}

type Actions = {
  addParticipant: (participant: HumanParticipant) => void;
  addVirtualParticipant: (participant: VirtualParticipant) => void;
  removeParticipant: (pid: string) => void;
  removeVirtualParticipant: (
    pid: string,
    type?: VirtualParticipant['type'],
  ) => void;
  clearParticipants: () => void;
  updateLocalParticipant: (participant: Partial<HumanParticipant>) => void;
  updateParticipant: (
    participantId: string,
    participant: Partial<HumanParticipant>,
  ) => void;
  updateParticipantRole: (pid: string, role: HumanParticipant['role']) => void;
  pinParticipant: (pid: string, type: Participant['type']) => void;
  unpinParticipant: () => void;
  getParticipantById: (pid: string) => HumanParticipant | undefined;
  reset: () => void;
};

const initialLocalParticipant: HumanParticipant = {
  avatar: undefined,
  isLocal: true,
  name: '',
  pid: 'local',
  ruid: 'local-human',
  role: 'none',
  sources: new Map(),
  type: 'human',
  uid: 'unknown',
};

const initialState: ParticipantsState = {
  dominantSpeakers: [],
  localParticipant: initialLocalParticipant,
  participants: [],
  virtualParticipants: [],
};

export const useParticipantStore = create<ParticipantsState & Actions>()(
  subscribeWithSelector(
    combine(initialState, (set, get) => ({
      addParticipant: participant => {
        set(state => ({
          participants: [...state.participants, participant],
        }));
      },
      addVirtualParticipant: (participant: VirtualParticipant) => {
        set(state => ({
          virtualParticipants: [...state.virtualParticipants, participant],
        }));
      },
      clearParticipants: () =>
        set({ participants: [], virtualParticipants: [] }),
      removeParticipant: pid => {
        const participants = useParticipantStore.getState().participants;
        const removedParticipant = participants.find(p => p.pid === pid);
        if (removedParticipant) {
          sendParticipantEvent(
            EVENT_LISTENER_METHODS.ParticipantEventsListner.onParticipantLeft,
            removedParticipant.pid,
          );

          // TODO: Maybe move this logic to the single place where pinning is handled
          const pinnedParticipant =
            useParticipantStore.getState().pinnedParticipant;
          if (pinnedParticipant?.pid === removedParticipant.pid) {
            useParticipantStore.getState().unpinParticipant();
          }
        }
        set(state => ({
          participants: state.participants.filter(p => p.pid !== pid),
        }));
      },
      removeVirtualParticipant: (
        pid: string,
        type: VirtualParticipant['type'] = 'screen-share',
      ) => {
        const pinnedParticipant =
          useParticipantStore.getState().pinnedParticipant;
        if (pinnedParticipant?.pid === pid && pinnedParticipant.type === type) {
          useParticipantStore.getState().unpinParticipant();
        }
        set(state => ({
          virtualParticipants: state.virtualParticipants.filter(
            p => !(p.pid === pid && p.type === type),
          ),
        }));
      },
      updateLocalParticipant: participant => {
        set(state => ({
          localParticipant: {
            ...state.localParticipant,
            ...participant,
          },
        }));
      },
      updateParticipant: (participantId, participant) => {
        set(state => {
          if (participantId === state.localParticipant.pid) {
            return {
              localParticipant: {
                ...state.localParticipant,
                ...participant,
              },
            };
          }
          return {
            participants: state.participants.map(p =>
              p.pid === participantId ? { ...p, ...participant } : p,
            ),
          };
        });
      },
      updateParticipantRole: (pid, role) => {
        set(state => ({
          participants: state.participants.map(p =>
            p.pid === pid ? { ...p, role } : p,
          ),
        }));
      },
      pinParticipant: (pid, type) => {
        const allParticipants = getAllParticipants();
        const participant = allParticipants.find(
          p => p.pid === pid && p.type === type,
        );
        if (participant) {
          set({ pinnedParticipant: participant });
        } else {
          console.warn(`Participant with id ${pid} not found to pin. `);
        }
      },
      unpinParticipant: () => {
        set({ pinnedParticipant: undefined });
      },
      getParticipantById: pid => {
        const localParticipant = get().localParticipant;
        if (pid === localParticipant.pid) {
          return localParticipant;
        }
        return get().participants.find(p => p.pid === pid);
      },
      reset: () => set(initialState),
    })),
  ),
);

export function useAllParticipants(): Participant[] {
  const localParticipant = useParticipantStore(state => state.localParticipant);
  const participants = useParticipantStore(state => state.participants);
  const virtualParticipants = useParticipantStore(
    state => state.virtualParticipants,
  );
  const iAmRecorder = useConfigStore.getState().iAmRecorder;

  return useMemo(() => {
    if (iAmRecorder) {
      return [...virtualParticipants, ...participants];
    }
    return [localParticipant, ...virtualParticipants, ...participants];
  }, [iAmRecorder, localParticipant, participants, virtualParticipants]);
}

export function getAllParticipants(): Participant[] {
  const localParticipant = useParticipantStore.getState().localParticipant;
  const participants = useParticipantStore.getState().participants;
  const virtualParticipants =
    useParticipantStore.getState().virtualParticipants;

  return [localParticipant, ...virtualParticipants, ...participants];
}

const updateDominantSpeakers = () => {
  const dominantSpeakers = useParticipantStore.getState().dominantSpeakers;
  const participants = useParticipantStore.getState().participants;
  useParticipantStore.setState({
    dominantSpeakers: dominantSpeakers.filter(pid =>
      participants.some(p => p.pid === pid),
    ),
  });
};

export const pinParticipant = (
  participantId: string,
  type: Participant['type'],
) => {
  useParticipantStore.getState().pinParticipant(participantId, type);
};

export const unpinParticipant = () => {
  useParticipantStore.getState().unpinParticipant();
};

export const togglePinParticipant = (
  participantId: string,
  type: Participant['type'],
) => {
  const pinnedParticipant = useParticipantStore.getState().pinnedParticipant;
  if (
    pinnedParticipant?.pid === participantId &&
    pinnedParticipant.type === type
  ) {
    useParticipantStore.getState().unpinParticipant();
  } else {
    useParticipantStore.getState().pinParticipant(participantId, type);
  }
};

useParticipantStore.subscribe(
  state => state.virtualParticipants,
  virtualParticipants => {
    console.log('Virtual participants updated:', virtualParticipants);
    if (virtualParticipants && virtualParticipants.length > 0) {
      setLayout(LAYOUT.SIDEBAR, true);
    } else {
      const sidebarLayoutSetProgrammatically =
        useBaseStore.getState().sidebarLayoutSetProgrammatically;
      if (sidebarLayoutSetProgrammatically) {
        setLayout(LAYOUT.TILE);
      }
    }
  },
);

useParticipantStore.subscribe(
  state => state.participants.length,
  () => {
    updateDominantSpeakers();
  },
);

useParticipantStore.subscribe(
  state =>
    state.participants.map(({ pid, name, avatar, uid }) => ({
      pid,
      name,
      avatar,
      uid,
    })),
  () => {
    sendParticipantListChangedEvent();
  },
  {
    equalityFn: isArrayOfObjEqual,
  },
);

useParticipantStore.subscribe(
  state => state.localParticipant.pid,
  localParticipantId => {
    useTracksStore.getState().updateLocalTrack(MEDIA_TYPE.AUDIO, {
      pid: localParticipantId,
    });
    useTracksStore.getState().updateLocalTrack(MEDIA_TYPE.VIDEO, {
      pid: localParticipantId,
    });
  },
);

useParticipantStore.subscribe(
  state => Boolean(state.pinnedParticipant),
  pinnedParticipant => {
    if (pinnedParticipant) {
      const layout = useBaseStore.getState().layout;
      if (layout === LAYOUT.TILE) {
        setLayout(LAYOUT.SIDEBAR, true);
      }
    } else {
      const sidebarLayoutSetProgrammatically =
        useBaseStore.getState().sidebarLayoutSetProgrammatically;
      if (sidebarLayoutSetProgrammatically) {
        setLayout(LAYOUT.TILE);
      }
    }
  },
);

useParticipantStore.subscribe(
  state => state.participants.length,
  participantsCount => {
    if (participantsCount === 0) {
      useBaseStore.getState().restartIdealTimeoutTimer();
    } else {
      useBaseStore.getState().clearIdealTimeoutTimer();
    }
  },
);

eventBus.subscribe(
  EVENT_LISTENER_METHODS.SessionStatusListener.onSessionJoined,
  () => {
    const participantsCount =
      useParticipantStore.getState().participants.length;
    if (participantsCount === 0) {
      useBaseStore.getState().restartIdealTimeoutTimer();
    }
  },
);

export const getMainParticipantFromState = (state: {
  pinnedParticipant?: Participant;
  participants: Participant[];
  virtualParticipants: Participant[];
  dominantSpeakers: string[];
  localParticipant: HumanParticipant;
}): Participant => {
  const {
    pinnedParticipant,
    participants,
    virtualParticipants,
    dominantSpeakers,
    localParticipant,
  } = state;

  // If there is a pinned participant, that participant is always the main participant
  if (pinnedParticipant) {
    return pinnedParticipant;
  }

  if (virtualParticipants.length > 0) {
    return virtualParticipants[0];
  }

  for (const dominantSpeakerId of dominantSpeakers) {
    const dominantSpeaker = participants.find(p => p.pid === dominantSpeakerId);
    if (dominantSpeaker) {
      return dominantSpeaker;
    }
  }

  // Find first participant excluding local participant
  if (participants.length > 0) {
    return participants[0];
  }

  // If no other participants, return local participant
  return localParticipant;
};

useParticipantStore.subscribe(
  state => state.participants.filter(p => p.uid),
  (participants, previousParticipants) => {
    if (participants.length > previousParticipants.length) {
      const participantsToAdd = differenceBy(
        participants,
        previousParticipants,
        x => x.uid,
      );
      participantsToAdd.forEach(participant => {
        sendParticipantEvent(
          EVENT_LISTENER_METHODS.ParticipantEventsListner.onParticipantJoined,
          participant.pid,
        );
      });
    }
  },
);
